import type { TaxCode } from "@/apis/gql/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEqual, noop } from "lodash";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { TaxCodeDropdownInput } from "@/components/shared/form/tax-code-dropdown-input";
import { Form } from "@/components/ui/form";

import { Badge } from "@/components/ui/badge";
import _ from "lodash";
import {
  Plane,
  ShieldCheck,
  Store,
  TriangleAlert,
  Undo2,
  Wrench,
} from "lucide-react";

enum SpecialFilterValue {
  Unknown = "unknown",
}

const formSchema = z.object({
  taxCodeId: z.string().min(1).max(255).nullable(),
  lineItemIds: z.array(z.string().min(1).max(255)),
});

export type FormValues = z.infer<typeof formSchema>;

type Props = {
  initialValues: {
    taxCodeId: string | null;
    lineItemIds: string[];
    buJustification?: string;
  };
  taxCodes: TaxCode[];
  placeholder?: string;
  fullWidth?: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
  onSubmit: (value: FormValues) => Promise<void> | void;
  onSuccess?: (value: string | null) => Promise<void> | void;
  onError?: (error: Error) => void;
  variant?: "outline" | "ghost";
};

export const TaxCodeForm = ({
  taxCodes,
  variant,
  className,
  initialValues,
  onSubmit = noop,
  onSuccess = noop,
  onError = noop,
  readOnly = false,
  fullWidth = false,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  const { reset } = form;

  useEffect(() => {
    if (!isEqual(form.getValues().taxCodeId, initialValues.taxCodeId)) {
      form.reset(initialValues);
    }
  }, [initialValues.taxCodeId]);

  const handleSubmit = async ({ lineItemIds, taxCodeId }: FormValues) => {
    const value = taxCodeId !== SpecialFilterValue.Unknown ? taxCodeId : null;

    const toastId = toast.loading(t("generic_loading"));

    try {
      await onSubmit({ lineItemIds, taxCodeId: value });

      await onSuccess(value);

      setTimeout(() => {
        toast.dismiss(toastId);
      }, 100);
    } catch (error) {
      onError(error as Error);
      toast.error(t("generic_error"), { richColors: true, id: toastId });
      reset(initialValues, { keepIsValid: true });
    }
  };

  return (
    <Form {...form}>
      <form>
        <TaxCodeDropdownInput
          name="taxCodeId"
          options={taxCodes}
          fullWidth={fullWidth}
          className={className}
          readOnly={readOnly}
          variant={variant}
          specialNotice={createSpecialNotice(initialValues?.buJustification)}
          disabled={disabled}
        />
        <AutoSaveForm onSubmit={handleSubmit} debounceTime={0} />
      </form>
    </Form>
  );
};

export enum BU_JUSTIFICATIONS {
  SMALL_BUSINESS = "Kleinunternehmer",
  INSURANCE = "Versicherungssteuer",
  B2C = "B2C Rechnung aus dem Ausland",
  LOCAL_SERVICE = "Ort der Erbringung außerhalb Deutschlands",
  THIRTEEN_B = "13b Sachv.",
  CONSTRUCTION = "Bauleistung",
}

const createSpecialNotice = (buJustification: string | undefined) => {
  switch (buJustification) {
    case BU_JUSTIFICATIONS.SMALL_BUSINESS:
      return (
        <Badge className="bg-transparent border border-gray-200 text-gray-500 text-xs h-4 inline-flex items-center justify-start hover:bg-transparent shadow-none whitespace-nowrap overflow-hidden px-1.5">
          <Store className="h-3 w-3 mr-1" />
          Kleinunternehmer
        </Badge>
      );
    case BU_JUSTIFICATIONS.INSURANCE:
      return (
        <Badge className="bg-transparent border border-gray-200 text-gray-500 text-xs h-4 inline-flex items-center justify-start hover:bg-transparent shadow-none whitespace-nowrap overflow-hidden px-1.5">
          <ShieldCheck className="h-3 w-3 mr-1" />
          Versicherungssteuer
        </Badge>
      );
    case BU_JUSTIFICATIONS.B2C:
      return (
        <Badge className="bg-transparent border border-gray-200 text-gray-500 text-xs h-4 inline-flex items-center justify-start hover:bg-transparent shadow-none whitespace-nowrap overflow-hidden px-1.5">
          <TriangleAlert className="h-3 w-3 mr-1" />
          USt. falsch
        </Badge>
      );
    case BU_JUSTIFICATIONS.LOCAL_SERVICE:
      return (
        <Badge className="bg-transparent border border-gray-200 text-gray-500 text-xs  h-4 inline-flex items-center justify-start hover:bg-transparent overflow-hidden whitespace-nowrap shadow-none px-1.5">
          <Plane className="h-3 w-3 mr-1" />
          Ort im Ausland
        </Badge>
      );
    case BU_JUSTIFICATIONS.THIRTEEN_B:
      return (
        <Badge className="bg-transparent border border-gray-200 text-gray-500 text-xs h-4 inline-flex items-center justify-start hover:bg-transparent overflow-hidden whitespace-nowrap shadow-none px-1.5">
          <Undo2 className="h-3 w-3 mr-1" />
          13b
        </Badge>
      );
    case BU_JUSTIFICATIONS.CONSTRUCTION:
      return (
        <Badge className="bg-transparent border border-gray-200 text-gray-500 text-xs h-4 inline-flex hover:bg-transparent overflow-hidden whitespace-nowrap shadow-none px-1.5">
          <Wrench className="h-3 w-3 mr-1" />
          Bauleistung
        </Badge>
      );

    default:
      return undefined;
  }
};
